import { useContext, useEffect, useState } from "react";
import {
    Alert, Box, Button, Card, Chip, CircularProgress, Divider, FormControl,
    FormLabel, IconButton, Input, Modal, ModalDialog, Option, Select, Sheet,
    Stack, Textarea, Typography,
} from "@mui/joy";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

import { usePartner } from "../../PartnerContext";
import {
    APIGetSupportTickets,
    APICreateSupportTicket,
    APIGetSupportTicket,
    APIReplySupportTicket,
    APICloseSupportTicket,
} from "../../api_partner";


const STATUS_COLORS = {
    open: 'warning',
    in_progress: 'primary',
    waiting_user: 'warning',
    closed: 'neutral',
};

const STATUS_LABELS = {
    open: 'Открыт',
    in_progress: 'В работе',
    waiting_user: 'Ждёт ответа',
    closed: 'Закрыт',
};

const CATEGORY_LABELS = {
    payout: 'Выплаты',
    tech: 'Технический',
    account: 'Аккаунт',
    other: 'Другое',
};


function NewTicketModal({ open, onClose, onCreated }) {
    const { partnerToken: token } = usePartner();
    const [form, setForm] = useState({ subject: '', category: 'other', text: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const submit = async () => {
        setError(null);
        if (!form.subject || !form.text) {
            setError('Заполните тему и сообщение');
            return;
        }
        try {
            setSubmitting(true);
            await APICreateSupportTicket(token, form);
            setForm({ subject: '', category: 'other', text: '' });
            onCreated?.();
            onClose();
        } catch (e) {
            setError(e?.response?.data?.error || 'Ошибка создания тикета');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ minWidth: 320, maxWidth: 500 }}>
                <Typography level="title-lg">Новый тикет</Typography>
                {error && <Alert color="danger">{error}</Alert>}
                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Тема</FormLabel>
                        <Input
                            value={form.subject}
                            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                            placeholder="Кратко суть"
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Категория</FormLabel>
                        <Select value={form.category} onChange={(_, v) => setForm(f => ({ ...f, category: v }))}>
                            <Option value="payout">Выплаты</Option>
                            <Option value="tech">Технический вопрос</Option>
                            <Option value="account">Аккаунт / реквизиты</Option>
                            <Option value="other">Другое</Option>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Сообщение</FormLabel>
                        <Textarea
                            minRows={4}
                            value={form.text}
                            onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                            placeholder="Опишите вопрос подробно"
                        />
                    </FormControl>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" color="neutral" onClick={onClose} fullWidth>Отмена</Button>
                        <Button onClick={submit} loading={submitting} fullWidth>Создать</Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


function TicketDetail({ ticket: initialTicket, onBack, onUpdate }) {
    const { partnerToken: token } = usePartner();
    const [ticket, setTicket] = useState(initialTicket);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);

    const reload = async () => {
        try {
            const res = await APIGetSupportTicket(token, ticket.id);
            setTicket(res?.ticket || ticket);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { reload(); }, []);

    const send = async () => {
        if (!reply.trim()) return;
        try {
            setSending(true);
            await APIReplySupportTicket(token, ticket.id, reply.trim());
            setReply('');
            await reload();
            onUpdate?.();
        } catch (e) { console.error(e); }
        finally { setSending(false); }
    };

    const close = async () => {
        try {
            await APICloseSupportTicket(token, ticket.id);
            await reload();
            onUpdate?.();
        } catch (e) { console.error(e); }
    };

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <IconButton variant="plain" onClick={onBack}><ArrowBackRoundedIcon /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography level="title-md">{ticket.subject}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip size="sm" variant="soft">{CATEGORY_LABELS[ticket.category]}</Chip>
                        <Chip size="sm" variant="soft" color={STATUS_COLORS[ticket.status]}>
                            {STATUS_LABELS[ticket.status]}
                        </Chip>
                    </Stack>
                </Box>
                {ticket.status !== 'closed' && (
                    <Button variant="outlined" color="neutral" size="sm" onClick={close}>
                        Закрыть
                    </Button>
                )}
            </Stack>

            <Divider sx={{ my: 1 }} />

            {/* Сообщения */}
            <Stack spacing={1} sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                {ticket.messages?.map(m => (
                    <Sheet
                        key={m.id}
                        variant={m.sender_is_admin ? 'soft' : 'outlined'}
                        color={m.sender_is_admin ? 'primary' : 'neutral'}
                        sx={{
                            p: 1.5,
                            borderRadius: 'sm',
                            alignSelf: m.sender_is_admin ? 'flex-start' : 'flex-end',
                            maxWidth: '85%',
                        }}
                    >
                        <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {m.sender_label}
                        </Typography>
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                            {format(new Date(m.date_create), 'd MMM, HH:mm', { locale: ru })}
                        </Typography>
                    </Sheet>
                ))}
                {(!ticket.messages || ticket.messages.length === 0) && (
                    <Typography level="body-sm" color="neutral">Сообщений нет</Typography>
                )}
            </Stack>

            {/* Ответ */}
            {ticket.status !== 'closed' ? (
                <Box sx={{ mt: 2 }}>
                    <Textarea
                        minRows={2}
                        placeholder="Ваше сообщение..."
                        value={reply}
                        onChange={e => setReply(e.target.value)}
                    />
                    <Button
                        startDecorator={<SendRoundedIcon />}
                        sx={{ mt: 1 }}
                        onClick={send}
                        loading={sending}
                        disabled={!reply.trim()}
                    >
                        Отправить
                    </Button>
                </Box>
            ) : (
                <Alert color="neutral" sx={{ mt: 2 }} startDecorator={<LockRoundedIcon />}>
                    Тикет закрыт
                </Alert>
            )}
        </Card>
    );
}


export default function PartnerSupport() {
    const { partnerToken: token } = usePartner();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTicket, setActiveTicket] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const res = await APIGetSupportTickets(token);
            setTickets(res?.results || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (token) load(); }, [token]);

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>;
    }

    if (activeTicket) {
        return (
            <TicketDetail
                ticket={activeTicket}
                onBack={() => { setActiveTicket(null); load(); }}
                onUpdate={load}
            />
        );
    }

    return (
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="title-md">Мои обращения</Typography>
                <Button startDecorator={<AddRoundedIcon />} onClick={() => setModalOpen(true)}>
                    Новый тикет
                </Button>
            </Stack>

            {tickets.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>У вас пока нет обращений</Typography>
                    <Typography level="body-xs" color="neutral">
                        Создайте тикет, чтобы связаться с поддержкой
                    </Typography>
                </Card>
            ) : (
                <Stack spacing={1}>
                    {tickets.map(t => (
                        <Sheet
                            key={t.id}
                            variant="outlined"
                            sx={{
                                p: 1.5, borderRadius: 'sm',
                                cursor: 'pointer',
                                transition: '.2s',
                                '&:hover': { borderColor: 'primary.300' },
                            }}
                            onClick={() => setActiveTicket(t)}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography level="title-sm">{t.subject}</Typography>
                                    {t.last_message && (
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                            sx={{
                                                mt: 0.3,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {t.last_message.sender_label}: {t.last_message.text}
                                        </Typography>
                                    )}
                                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.3 }}>
                                        {format(new Date(t.date_create), 'd MMM yyyy', { locale: ru })}
                                    </Typography>
                                </Box>
                                <Stack direction="column" spacing={0.5} alignItems="flex-end">
                                    <Chip size="sm" color={STATUS_COLORS[t.status]} variant="soft">
                                        {STATUS_LABELS[t.status]}
                                    </Chip>
                                    <Chip size="sm" variant="outlined">
                                        {CATEGORY_LABELS[t.category]}
                                    </Chip>
                                </Stack>
                            </Stack>
                        </Sheet>
                    ))}
                </Stack>
            )}

            <NewTicketModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreated={load}
            />
        </Stack>
    );
}
